import { graphql } from 'app/generated/gql'
import { SortDirection } from 'app/generated/graphql'
import { useLocation, useSearchParams } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'
import { compact, uniq } from 'lodash'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useContactListModal } from 'app/components/contact-list-modal/useContactListModal'
import { DateComponent } from 'app/components/DateComponent'
import { ContactCell } from 'app/components/shared/ContactCell'
import { AnchorCell } from 'app/components/table/AnchorCell'
import { LazyLocationCard } from 'app/components/shared/LazyLocationCard'
import TagsCell from 'app/components/tags/TagsCell'
import {
  decodeFilters,
  encodeFilters,
  convertUrlFiltersToES,
  FilterableColumn,
  FilterObject,
  FilterOperator,
  FilterValueType,
  PredefinedFilter,
} from 'app/filter.utils'
import { useAddMarketingColumns } from 'app/hooks/useAddMarketingColumns'
import { gqlFetchList, gqlQuery } from 'app/lib/graphqlClient'
import { nullsToUndefined } from 'app/lib/nullsToUndefined'
import { tagFilterOptionsFetcher } from 'app/lib/fetchers/tagFilterOptionsFetcher'
import { ActivityJson } from 'app/types/entities'
import { ContactJson } from 'app/types/entities'
import { notifyError } from 'app/services/notify.client'
import type { SearchResponse } from 'app/services/search'
import { useActivityStore, useContactStore } from 'app/stores/StoreContext'
import { aiAgentColumns, getAiAgentFilterableColumns, withAiAgentDefaultVisibility } from 'app/table-columns/activities'
import { appointmentColumns } from 'app/table-columns/appointments'
import type { AppointmentRow } from 'app/types/appointment-row'
import { EMPTY_PROVIDER_DIRECTORY, contactsForPatients, patientLinks } from 'lib/appointmentsPage'
import { bg } from 'lib/background'
import { currentUrlWithParam } from 'lib/currentUrlWithParam'
import { getLeadStatusColorMap } from 'lib/colorMappings'
import { comActivityTypes } from 'lib/comActivityTypes'
import { debug, logError } from 'lib/logging'
import { formatPercent } from 'app/lib/utils'
import { StatsCardType } from 'types/StatsCardType'
import { usPhoneNumber } from 'lib/phone'

const VOICE_AGENT_METRICS_QUERY = graphql(`
  query VoiceAgentMetrics($dateRange: DateRangeInput!, $locationIds: [String!]) {
    voiceAgentMetrics(dateRange: $dateRange, locationIds: $locationIds) {
      scheduleRate
      newPatientCalls
      appointmentsBooked
      appointmentsCompleted
      totalCalls
      locationCount
    }
  }
`)

const AGENT_BOOKINGS_COUNT_QUERY = graphql(`
  query AgentBookingsCount($search: String) {
    appointments(search: $search, pagination: { limit: 1 }) {
      total
    }
  }
`)

const AGENT_APPOINTMENTS_QUERY = graphql(`
  query AgentAppointments($search: String, $pagination: PaginationInput, $sort: SortInput) {
    appointments(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        amount
        appointmentMadeDate
        confirmed
        createdDate
        date
        description
        duration
        id
        locationId
        note
        patientId
        pmsStatus
        procedureCode1
        procedureCode1Amount
        procedureCode1Time
        procedureCode2
        procedureCode2Amount
        procedureCode2Time
        procedureCode3
        procedureCode3Amount
        procedureCode3Time
        procedureCodes
        providerId
        sikkaOfficeId
        sikkaPracticeId
        source
        status
        time
        timeToAppointment
        type
        updatedDate
        version
      }
      total
    }
  }
`)

const AGENT_PATIENT_CONTACTS_QUERY = graphql(`
  query AgentPatientContacts($search: String, $pagination: PaginationInput) {
    contacts(search: $search, pagination: $pagination) {
      hits {
        id
        name
        patientIds
      }
    }
  }
`)

export type MetricFilter = `newPatientCalls` | `appointmentsBooked` | `appointmentsCompleted` | null

export const DISQUALIFYING_TAGS = [
  `Insurance: State / HMO`,
  `Insurance: Out of Network`,
  `Service Not Offered`,
  `Office Too Far`,
]

const DISQUALIFYING_TAGS_LOWER = DISQUALIFYING_TAGS.map((t) => t.toLowerCase())

export function isNewPatientCall(a: { tags?: string[] }): boolean {
  return a.tags?.some((t) => t.toLowerCase().includes(`new patient`)) ?? false
}

export function isScheduledCall(a: { tags?: string[] }): boolean {
  return (
    a.tags?.some((t) => {
      const tag = t.toLowerCase()
      return tag === `appointment scheduled` || tag === `appointment rescheduled` || tag === `appointment confirmation`
    }) ?? false
  )
}

export function isOfficeTransfer(a: { tags?: string[] }): boolean {
  return a.tags?.some((t) => t.toLowerCase() === `office transfer`) ?? false
}

export function isDisqualifiedCall(a: { tags?: string[] }): boolean {
  return a.tags?.some((t) => DISQUALIFYING_TAGS_LOWER.includes(t.toLowerCase())) ?? false
}

export function isExcludedCall(a: { tags?: string[] }): boolean {
  return isOfficeTransfer(a) || isDisqualifiedCall(a)
}

const METRIC_TITLES: Record<Exclude<MetricFilter, null>, string> = {
  newPatientCalls: `New Patient Calls`,
  appointmentsBooked: `Appointments Booked`,
  appointmentsCompleted: `Appointments Completed`,
}

const ACTIVITY_FIELDS = `
  id
  orgId
  workspaceId
  locationId
  type
  subType
  typeId
  activityDate
  contactName
  contactPhone
  phoneFromActivity
  description
  needsReview
  note
  tags
  contacts { id name phone lifecycleStage locationId }
  owner { id name }
  channel
  vendor
  trackingNumber
  trackingNumberName
  utmSource
  utmMedium
  utmCampaign
  utmKeywords
  utmContent
`

const CONTACT_FIELDS = `
  id
  name
  locationId
  orgId
  workspaceId
  leadStatus
  lifecycleStage
  lastCommDate
  patientIds
  tags
  cellPhone
  homePhone
  workPhone
  email
`

type VoiceAgentMetrics = {
  scheduleRate: number
  newPatientCalls: number
  appointmentsBooked: number
  appointmentsCompleted: number
  totalCalls: number
  locationCount: number
}

const AGENT_APPOINTMENT_COLUMNS = [`patientName`, `date`, `status`, `type`, `locationId`, `appointmentMadeDate`]

export const defaultTagsFilter: PredefinedFilter = {
  tags: {
    operator: `in`,
    value: [`New Patient`],
    readOnly: false,
  },
}

export const defaultExcludeFilters: FilterObject[] = [
  {
    attribute: `tags`,
    operator: `in` as FilterOperator,
    value: [`New Patient`],
    displayName: `Tags`,
    type: FilterValueType.stringArray,
  },
  {
    attribute: `tags`,
    operator: `not_in` as FilterOperator,
    value: [...DISQUALIFYING_TAGS, `Office Transfer`],
    displayName: `Tags`,
    type: FilterValueType.stringArray,
  },
]

export const defaultFiltersEncoded = encodeFilters(defaultExcludeFilters)

export const CALL_LOG_TAB = `Call Log`

export const VOICE_AGENT_REMOVED_FILTER_ATTRIBUTES = new Set([`locationId`])

export type UseAiAgentVoiceReturn = {
  locationCount: number
  activitiesState: SearchResponse<ActivityJson> | undefined
  metricsData: any
  npCallActivities: ActivityJson[]
  isNpCallsLoading: boolean
  dateRange: { fromDate: string; toDate: string }
  stats: StatsCardType[]
  columns: any[]
  filterableColumns: FilterableColumn[]
  focusedActivity: ActivityJson | undefined
  openDrawer: boolean
  isActivityLoading: boolean
  focusedContact: ContactJson | undefined
  openContactDrawer: boolean
  metricFilter: MetricFilter
  metricModalTitle: string
  modalActivity: ActivityJson | undefined
  modalActivityDrawerOpen: boolean
  modalActivityContact: ContactJson | undefined
  modalActivityContactDrawerOpen: boolean
  modalContacts: ContactJson[]
  modalContactsLoading: boolean
  /** The appointments behind Appointments Booked or Completed, one row each so the count matches the card. */
  modalAppointments: AppointmentRow[]
  appointmentModalColumns: ColumnDef<AppointmentRow, any>[]
  /** Appointments the agent created in the range; null until read. */
  bookedCount: number | null
  modal: ReturnType<typeof useContactListModal>
  filteredNpCallActivities: ActivityJson[]
  npSearchValue: string
  npFilterValue: string
  modalActivityColumns: any[]
  contactModalColumns: any[]
  contactModalFilterableColumns: FilterableColumn[]
  loadActivity: (id: string) => Promise<void>
  activityHandler: (activity: ActivityJson) => void
  closeDrawerHandler: () => void
  handleContactClick: (contactId: string) => Promise<void>
  closeContactDrawer: () => void
  openMetricModal: (filter: MetricFilter) => void
  closeMetricModal: () => void
  modalActivityHandler: (activity: ActivityJson) => void
  closeModalActivityDrawer: () => void
  handleModalActivityContactClick: (contactId: string) => void
  closeModalActivityContactDrawer: () => void
  setNpSearchValue: (v: string) => void
  setNpFilterValue: (v: string) => void
  dateChangeHandler: (
    fromDate: string,
    toDate: string,
    _compareFromDate: string,
    _compareToDate: string,
    _comparingGoals: boolean,
    _dontCompare: boolean,
  ) => void
}

export function useAiAgentVoice(): UseAiAgentVoiceReturn {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const baseUrl = `${location.pathname}${location.search.replace(/(\?|&)drawer=([\w-]+)/, ``)}`
  const addMarketingColumns = useAddMarketingColumns()
  const contactStore = useContactStore()
  const activityStore = useActivityStore()

  const [locationCount, setLocationCount] = useState(0)
  const [activitiesData, setActivitiesData] = useState<SearchResponse<ActivityJson>>()
  const [metricsData, setMetricsData] = useState<VoiceAgentMetrics>()
  const [npCallActivities, setNpCallActivities] = useState<ActivityJson[]>([])

  const [focusedActivity, setFocusedActivity] = useState<ActivityJson>()
  const [openDrawer, setOpenDrawer] = useState(false)
  const [isActivityLoading, setIsActivityLoading] = useState(false)
  const [activityOverrides, setActivityOverrides] = useState<Record<string, ActivityJson>>({})
  const [focusedContact, setFocusedContact] = useState<ContactJson>()
  const [openContactDrawer, setOpenContactDrawer] = useState(false)

  const [metricFilter, setMetricFilter] = useState<MetricFilter>(null)
  const [modalActivity, setModalActivity] = useState<ActivityJson>()
  const [modalActivityDrawerOpen, setModalActivityDrawerOpen] = useState(false)
  const [modalActivityContact, setModalActivityContact] = useState<ContactJson>()
  const [modalActivityContactDrawerOpen, setModalActivityContactDrawerOpen] = useState(false)
  // activitiesData, metricsData, metricContactIdsData are now set via fetch above

  const [modalContacts, setModalContacts] = useState<ContactJson[]>([])
  const [modalContactsLoading, setModalContactsLoading] = useState(false)
  const [modalAppointments, setModalAppointments] = useState<AppointmentRow[]>([])
  const [bookedCount, setBookedCount] = useState<number | null>(null)
  const [isNpCallsLoading, setIsNpCallsLoading] = useState(true)

  const filterableColumns = useMemo(
    () =>
      getAiAgentFilterableColumns({
        comActivityTypes,
        preAppliedFilters: defaultTagsFilter,
        addMarketingColumns,
      }).filter((col) => !VOICE_AGENT_REMOVED_FILTER_ATTRIBUTES.has(col.attribute)),
    [addMarketingColumns],
  )

  function getDateRange(): { fromDate: string; toDate: string } {
    return {
      fromDate: searchParams.get(`fromDate`) || moment().startOf(`month`).format(`YYYY-MM-DD`),
      toDate: searchParams.get(`toDate`) || moment().endOf(`month`).format(`YYYY-MM-DD`),
    }
  }

  function getLocationIds(): string[] {
    return compact(searchParams.get(`locationIds`)?.split(`,`) || [])
  }

  // Normalize a bare 10-digit phone query into a field-scoped clause. Users copy
  // phone numbers straight out of the table as "(608) 467-0732"; passed raw into
  // the ES query_string the parens/hyphen are parsed as syntax and match nothing.
  // Same normalization as the Activities page (TRK-5304).
  function normalizeSearchQuery(query: string | null): string | undefined {
    if (!query) return undefined
    const digits = usPhoneNumber(query)
    return digits ? `(contactPhone:${digits} OR phoneFromActivity:${digits} OR contacts.phone:${digits})` : query
  }

  function buildActivitySearch(extraTerms: string[] = []): string {
    const { fromDate, toDate } = getDateRange()
    const locationIds = getLocationIds()
    const filterTerms = convertUrlFiltersToES(
      searchParams.get(`filters`) ?? defaultFiltersEncoded,
      undefined,
      filterableColumns,
    )
    return compact([
      `type:Call`,
      `tags:AI`,
      `activityDate:[${fromDate} TO ${toDate}]`,
      locationIds.length ? `locationId:(${locationIds.join(` OR `)})` : undefined,
      normalizeSearchQuery(searchParams.get(`query`)),
      ...filterTerms,
      ...extraTerms,
    ]).join(` `)
  }

  /** The agent's appointments created in the range, as Appointments Completed counts them on the API. */
  function agentAppointmentSearch(extraTerms: string[] = []): string {
    const { fromDate, toDate } = getDateRange()
    const locationIds = getLocationIds()
    return compact([
      `aiAgent:true`,
      `createdDate:[${fromDate} TO ${toDate}]`,
      locationIds.length ? `locationId:(${locationIds.join(` OR `)})` : undefined,
      ...extraTerms,
    ]).join(` `)
  }

  function buildBaseActivitySearch(extraTerms: string[] = []): string {
    const { fromDate, toDate } = getDateRange()
    const locationIds = getLocationIds()
    return compact([
      `type:Call`,
      `tags:AI`,
      `activityDate:[${fromDate} TO ${toDate}]`,
      locationIds.length ? `locationId:(${locationIds.join(` OR `)})` : undefined,
      ...extraTerms,
    ]).join(` `)
  }

  // Fetch the main table and aggregate stat-card metrics from GraphQL.
  useEffect(() => {
    let active = true
    const { fromDate, toDate } = getDateRange()
    const locationIds = getLocationIds()
    bg(async () => {
      const [activities, metricsResult, booked] = await Promise.all([
        gqlFetchList<ActivityJson>(
          `activities`,
          {
            search: buildActivitySearch(),
            pagination: { limit: 1000 },
            sort: { field: `activityDate`, direction: `desc` },
          },
          ACTIVITY_FIELDS,
        ),
        gqlQuery(VOICE_AGENT_METRICS_QUERY, {
          dateRange: { fromDate, toDate },
          locationIds: locationIds.length ? locationIds : undefined,
        }),
        gqlQuery(AGENT_BOOKINGS_COUNT_QUERY, { search: agentAppointmentSearch() }),
      ])
      if (!active) return
      if (metricsResult.error) {
        logError(`Error fetching voice agent metrics`, metricsResult.error.message)
        notifyError(`Error loading AI voice metrics`)
      }
      setActivitiesData(activities as unknown as SearchResponse<ActivityJson>)
      setMetricsData(metricsResult.data?.voiceAgentMetrics)
      setBookedCount(booked.data?.appointments.total ?? null)
      setLocationCount(metricsResult.data?.voiceAgentMetrics.locationCount ?? 0)
    })
    return () => {
      active = false
    }
  }, [searchParams.toString(), filterableColumns])

  // Always fetch qualified New Patient calls for the trend chart. Uses the same
  // definition as the `newPatientCalls` metric so the chart total ties to the
  // stat card; the New Patient Calls drill-in modal reuses this same set.
  //
  // Deliberate tradeoff: this is a second unconditional `limit:1000` `activities`
  // query that fires alongside the main table fetch on every date/filter change.
  // It can't be derived from `activitiesState.hits` — that set is `type:Call`
  // scoped (all calls, capped at 1000 most-recent), not NP-qualified, so it would
  // both mis-count and drop older NP calls in range. The always-visible chart
  // justifies the eager fetch.
  useEffect(() => {
    let active = true
    setIsNpCallsLoading(true)
    bg(async () => {
      const npCalls = await gqlFetchList<ActivityJson>(
        `activities`,
        {
          search: buildBaseActivitySearch([
            `tags:"New Patient"`,
            `NOT tags:(${[...DISQUALIFYING_TAGS, `Office Transfer`].map((t) => `"${t}"`).join(` OR `)})`,
          ]),
          pagination: { limit: 1000 },
          sort: { field: `activityDate`, direction: `desc` },
        },
        ACTIVITY_FIELDS,
      )
      if (!active) return
      setNpCallActivities(npCalls.hits)
      setIsNpCallsLoading(false)
    })
    return () => {
      active = false
    }
  }, [searchParams.toString(), filterableColumns])

  // The Call Log tab reads the same `filters` param and must open unfiltered.
  useEffect(() => {
    if (!searchParams.has(`filters`) && searchParams.get(`tab`) !== CALL_LOG_TAB) {
      setSearchParams((prev) => {
        prev.set(`filters`, defaultFiltersEncoded)
        return prev
      })
    }
  }, [])

  function updateUrlParam(key: string, value: string | null): void {
    const url = new URL(document.location.href)
    if (value) {
      url.searchParams.set(key, value)
    } else {
      url.searchParams.delete(key)
    }
    history.replaceState({}, ``, url.toString())
  }

  useEffect(() => {
    setActivityOverrides({})
  }, [activitiesData])

  const activitiesState = useMemo(() => {
    if (!activitiesData) return undefined
    const hasOverrides = Object.keys(activityOverrides).length > 0
    if (!hasOverrides) return activitiesData
    return {
      ...activitiesData,
      hits: activitiesData.hits.map((act) => activityOverrides[act.id!] ?? act),
    } as SearchResponse<ActivityJson>
  }, [activitiesData, activityOverrides])

  useEffect(() => {
    if (!metricFilter) {
      setModalContacts([])
      return
    }

    let active = true
    setModalContactsLoading(true)

    async function fetchModalData() {
      try {
        if (metricFilter === `newPatientCalls`) {
          // npCallActivities is fetched independently for the trend chart; the
          // New Patient Calls modal reuses that same set, so no fetch is needed.
          if (!active) return
          setModalContacts([])
          return
        }

        const appointmentsResult = await gqlQuery(AGENT_APPOINTMENTS_QUERY, {
          search: agentAppointmentSearch(metricFilter === `appointmentsCompleted` ? [`status:Completed`] : []),
          pagination: { limit: 1000 },
          sort: { field: `createdDate`, direction: SortDirection.Desc },
        })
        if (appointmentsResult.error) throw appointmentsResult.error
        const appointments = (appointmentsResult.data?.appointments.hits ?? []).map(nullsToUndefined)
        const patientIds = uniq(compact(appointments.map((appointment) => appointment.patientId)))
        const contactsResult = patientIds.length
          ? await gqlQuery(AGENT_PATIENT_CONTACTS_QUERY, {
              search: contactsForPatients(patientIds),
              pagination: { limit: patientIds.length * 3 },
            })
          : undefined
        if (contactsResult?.error) throw contactsResult.error
        const contacts = (contactsResult?.data?.contacts.hits ?? []).map(nullsToUndefined)
        const links = patientLinks(contacts, patientIds)
        if (!active) return
        setModalAppointments(
          appointments.map((appointment) => ({
            ...appointment,
            patient: appointment.patientId ? links.get(appointment.patientId) : null,
          })),
        )
      } catch (e) {
        logError(`Error fetching voice agent modal data`, e)
        notifyError(`Error loading contacts`)
      } finally {
        if (active) setModalContactsLoading(false)
      }
    }

    bg(fetchModalData())
    return () => {
      active = false
    }
  }, [metricFilter, searchParams.toString()])

  const modal = useContactListModal({ allContacts: modalContacts, onUrlParamChange: updateUrlParam })

  const [npSearchValue, setNpSearchValue] = useState(``)
  const [npFilterValue, setNpFilterValue] = useState(``)

  const filteredNpCallActivities = useMemo((): ActivityJson[] => {
    let result = npCallActivities

    if (npSearchValue) {
      const query = npSearchValue.toLowerCase()
      result = result.filter((a) => {
        const contactName = (a.contacts?.[0] as { id?: string; name?: string })?.name || ``
        const ownerName = (a.owner as { name?: string })?.name || ``
        return (
          contactName.toLowerCase().includes(query) ||
          a.tags?.some((t) => t.toLowerCase().includes(query)) ||
          ownerName.toLowerCase().includes(query)
        )
      })
    }

    if (npFilterValue) {
      const filters = decodeFilters(npFilterValue)
      for (const filter of filters) {
        const attr = filter.attribute.replace(`.keyword`, ``)
        result = result.filter((a) => {
          const raw = a[attr as keyof ActivityJson]
          const isArray = Array.isArray(raw)
          const strVal = isArray ? `` : (typeof raw === `string` ? raw : ``).toLowerCase()
          const arrVal: string[] = isArray ? (raw as string[]).map((v) => String(v).toLowerCase()) : []

          switch (filter.operator) {
            case `exactly`:
            case `not_exactly`: {
              const target = String(filter.value).toLowerCase()
              const match = isArray ? arrVal.includes(target) : strVal === target
              return filter.operator === `exactly` ? match : !match
            }
            case `contains`:
            case `not_contains`: {
              const target = String(filter.value).toLowerCase()
              const match = isArray ? arrVal.some((v) => v.includes(target)) : strVal.includes(target)
              return filter.operator === `contains` ? match : !match
            }
            case `in`: {
              if (!Array.isArray(filter.value)) return true
              const targets = filter.value.map((v) => v.toLowerCase())
              return isArray ? targets.some((t) => arrVal.includes(t)) : targets.includes(strVal)
            }
            case `not_in`: {
              if (!Array.isArray(filter.value)) return true
              const targets = filter.value.map((v) => v.toLowerCase())
              return isArray ? !targets.some((t) => arrVal.includes(t)) : !targets.includes(strVal)
            }
            default:
              return true
          }
        })
      }
    }

    return result
  }, [npCallActivities, npSearchValue, npFilterValue])

  const modalActivityHandler = (activity: ActivityJson) => {
    if (modalActivity?.id === activity.id) return
    setModalActivityContact(undefined)
    setModalActivityContactDrawerOpen(false)
    setModalActivity(activity)
    setModalActivityDrawerOpen(true)
  }

  const closeModalActivityDrawer = () => {
    setModalActivity(undefined)
    setModalActivityDrawerOpen(false)
  }

  const handleModalActivityContactClick = (contactId: string) => {
    setModalActivityDrawerOpen(false)
    bg(async () => {
      const result = await contactStore.fetchOne(contactId)
      const response = (result.data as any)?.contact as ContactJson | undefined
      if (!response || result.error) {
        logError(`Error loading contact`, (result.error as any)?.message)
        notifyError(`Error loading contact`)
        return
      }
      setModalActivityContact(response)
      setModalActivityContactDrawerOpen(true)
    })
  }

  const closeModalActivityContactDrawer = () => {
    setModalActivityContact(undefined)
    setModalActivityContactDrawerOpen(false)
    closeModalActivityDrawer()
  }

  useEffect(() => {
    const activityId = searchParams.get(`drawer`)
    if (activityId && !focusedActivity) {
      setIsActivityLoading(true)
      bg(async () => {
        const result = await activityStore.fetchOne(activityId)
        const activity = (result.data as any)?.activity as ActivityJson | undefined
        if (result.error || !activity) {
          const message = result.error?.message ?? `not found`
          logError(`Error fetching activity`, message)
          notifyError(`Activity ${message}`)
          setFocusedActivity(undefined)
          setOpenDrawer(false)
        } else {
          setFocusedActivity(activity)
        }
        setIsActivityLoading(false)
      })
    }
  }, [])
  useEffect(() => {
    const details = searchParams.get(`details`) as MetricFilter
    if (details && [`newPatientCalls`, `appointmentsBooked`, `appointmentsCompleted`].includes(details)) {
      setMetricFilter(details)
      const contactId = searchParams.get(`contact`)
      if (contactId) {
        bg(modal.handleContactClick(contactId))
      }
    }
  }, [])

  useEffect(() => {
    setOpenDrawer(!!focusedActivity)
  }, [focusedActivity])

  function activityHandler(activity: ActivityJson): void {
    if (focusedActivity?.id === activity.id) return
    debug(`activities`, `activityHandler`, activity)
    setFocusedContact(undefined)
    setOpenContactDrawer(false)
    setFocusedActivity(activity)
    updateUrlParam(`drawer`, activity.id!)
  }

  function closeDrawerHandler(): void {
    setFocusedActivity(undefined)
    setOpenDrawer(false)
    updateUrlParam(`drawer`, null)
  }

  async function loadActivity(id: string): Promise<void> {
    const result = await activityStore.fetchOne(id)
    const activity = (result.data as any)?.activity as ActivityJson | undefined
    if (result.error || !activity) {
      if (result.error) {
        logError(`Error loading activity`, result.error.message)
        notifyError(`Activity not saved ${result.error.message}`)
      }
      return
    }
    setFocusedActivity(activity)
    setActivityOverrides((prev) => ({ ...prev, [activity.id!]: activity }))
  }

  async function handleContactClick(contactId: string): Promise<void> {
    setOpenDrawer(false)
    const result = await contactStore.fetchOne(contactId)
    const response = (result.data as any)?.contact as ContactJson | undefined
    if (!response || result.error) {
      logError(`Error loading contact`, (result.error as any)?.message)
      notifyError(`Error loading contact`)
      return
    }
    setFocusedContact(response)
    setOpenContactDrawer(true)
  }

  function closeContactDrawer(): void {
    setFocusedContact(undefined)
    setOpenContactDrawer(false)
    closeDrawerHandler()
  }

  function openMetricModal(filter: MetricFilter): void {
    setMetricFilter(filter)
    modal.reset()
    setModalActivity(undefined)
    setModalActivityDrawerOpen(false)
    setModalActivityContact(undefined)
    setModalActivityContactDrawerOpen(false)
    setNpSearchValue(``)
    setNpFilterValue(``)
    updateUrlParam(`details`, filter)
    updateUrlParam(`contact`, null)
  }

  function closeMetricModal(): void {
    setMetricFilter(null)
    modal.reset()
    setModalActivity(undefined)
    setModalActivityDrawerOpen(false)
    setModalActivityContact(undefined)
    setModalActivityContactDrawerOpen(false)
    setNpSearchValue(``)
    setNpFilterValue(``)
    updateUrlParam(`details`, null)
    updateUrlParam(`contact`, null)
  }

  const metricModalTitle = metricFilter ? METRIC_TITLES[metricFilter] : ``

  function dateChangeHandler(
    fromDate: string,
    toDate: string,
    _compareFromDate: string,
    _compareToDate: string,
    _comparingGoals: boolean,
    _dontCompare: boolean,
  ): void {
    setSearchParams((prev) => {
      if (fromDate) prev.set(`fromDate`, fromDate)
      if (toDate) prev.set(`toDate`, toDate)
      if (!prev.has(`filters`) && prev.get(`tab`) !== CALL_LOG_TAB) prev.set(`filters`, defaultFiltersEncoded)
      return prev
    })
  }

  const stats: StatsCardType[] = useMemo(
    () => [
      {
        label: `New Patient Schedule Rate`,
        value: `${formatPercent(metricsData?.scheduleRate)}%`,
        infoTooltip: {
          title: `New Patient Schedule Rate`,
          message: `Percentage of qualified new patient calls that resulted in a scheduled appointment.`,
        },
      },
      {
        label: `New Patient Calls`,
        value: (metricsData?.newPatientCalls ?? 0).toLocaleString(`en-US`),
        infoTooltip: {
          title: `New Patient Calls`,
          message: `Total number of qualified calls with a new patient tag within the selected date range.`,
        },
        onOpenClick: () => openMetricModal(`newPatientCalls`),
      },
      {
        label: `Appointments Booked`,
        value: (bookedCount ?? metricsData?.appointmentsBooked ?? 0).toLocaleString(`en-US`),
        infoTooltip: {
          title: `Appointments Booked`,
          message: `Shows contacts that have appointments that were created via the agent.`,
        },
        onOpenClick: () => openMetricModal(`appointmentsBooked`),
      },
      {
        label: `Appointments Completed`,
        value: (metricsData?.appointmentsCompleted ?? 0).toLocaleString(`en-US`),
        infoTooltip: {
          title: `Appointments Completed`,
          message: `Total AI-booked appointments that have been marked as completed.`,
        },
        onOpenClick: () => openMetricModal(`appointmentsCompleted`),
      },
    ],
    [metricsData],
  )

  const columns = useMemo(
    () =>
      withAiAgentDefaultVisibility(
        aiAgentColumns(addMarketingColumns, baseUrl, activityHandler, () => {}, true, handleContactClick),
      ),
    [baseUrl, addMarketingColumns],
  )

  const contactModalColumns = useMemo(
    () => [
      {
        id: `name`,
        header: `Name`,
        accessorFn: (row: ContactJson) => row.name,
        meta: {
          defaultVisible: true,
          ControlledComponent: (row: ContactJson) => (
            // `contact`, not `drawer`: this page reopens the modal's contact from that param on load
            <AnchorCell
              href={currentUrlWithParam(`contact`, row.id!)}
              tooltipMessage={row.name ?? ``}
              clickHandler={() => bg(modal.handleContactClick(row.id!))}
            >
              <ContactCell contact={row} />
            </AnchorCell>
          ),
        },
      },
      {
        id: `locationId`,
        header: `Location`,
        accessorFn: (row: ContactJson) => row.locationId,
        meta: { defaultVisible: true },
        cell: ({ row }: { row: { original: ContactJson } }) => (
          <LazyLocationCard locationId={row.original.locationId} />
        ),
      },
      {
        id: `tags`,
        header: `Tags`,
        // ControlledComponent: DataTable's cell: path wraps content in an
        // ellipsis Tooltip that collapses TagsCell width (TRK-5317).
        meta: {
          defaultVisible: true,
          ControlledComponent: (row?: ContactJson) => (
            <TagsCell tags={row?.tags} workspaceId={row?.workspaceId} model="contacts" modelId={row?.id!} />
          ),
        },
        enableSorting: false,
      },
      {
        id: `leadStatus`,
        header: `Lead Status`,
        accessorFn: (row: ContactJson) => row.leadStatus,
        meta: { defaultVisible: true },
        cell: ({ row }: { row: { original: ContactJson } }) =>
          row.original.leadStatus ? (
            <div className="flex items-center gap-1.5">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: getLeadStatusColorMap(row.original.leadStatus) }}
              />
              <span>{row.original.leadStatus}</span>
            </div>
          ) : (
            `-`
          ),
      },
      {
        id: `lifecycleStage`,
        header: `Lifecycle`,
        accessorFn: (row: ContactJson) => row.lifecycleStage,
        meta: { defaultVisible: true },
        cell: ({ row }: { row: { original: ContactJson } }) => row.original.lifecycleStage || `-`,
      },
      {
        id: `lastCommDate`,
        header: `Last Communication Date`,
        accessorFn: (row: ContactJson) => row.lastCommDate,
        meta: { defaultVisible: true },
        cell: ({ row }: { row: { original: ContactJson } }) => (
          <DateComponent value={row.original.lastCommDate} dateOnly />
        ),
      },
    ],
    [],
  )

  const contactModalFilterableColumns: FilterableColumn[] = useMemo(
    () => [
      { attribute: `name`, displayName: `Name`, type: FilterValueType.string },
      {
        attribute: `locationId`,
        displayName: `Location`,
        optionsSource: `/api/locations`,
        type: FilterValueType.id,
        uniqOperator: `in`,
      } as FilterableColumn,
      {
        attribute: `tags`,
        displayName: `Tags`,
        fetcher: (params) => tagFilterOptionsFetcher(`Contact`, params),
        type: FilterValueType.stringArray,
      },
      {
        attribute: `leadStatus.keyword`,
        displayName: `Lead Status`,
        type: FilterValueType.multiple,
        options: [`Needs Action`, `In Progress`, `Scheduled`, `Closed Won`, `Closed Lost`].map((o) => ({
          id: o,
          name: o,
          value: o,
        })),
      },
      {
        attribute: `lifecycleStage.keyword`,
        displayName: `Lifecycle Stage`,
        type: FilterValueType.multiple,
        options: [`Lead`, `Qualified Lead`, `New Patient Scheduled`, `Patient`].map((o) => ({
          id: o,
          name: o,
          value: o,
        })),
      },
    ],
    [],
  )

  const modalActivityColumns = useMemo(
    () =>
      aiAgentColumns(
        addMarketingColumns,
        baseUrl,
        modalActivityHandler,
        () => {},
        true,
        handleModalActivityContactClick,
      ),
    [baseUrl, addMarketingColumns],
  )

  const appointmentModalColumns = appointmentColumns({
    onOpenPatient: handleModalActivityContactClick,
    providers: EMPTY_PROVIDER_DIRECTORY,
    withYear: false,
    today: new Date(),
  }).filter((column) => AGENT_APPOINTMENT_COLUMNS.includes(column.id!))

  return {
    locationCount,
    activitiesState,
    metricsData,
    npCallActivities,
    isNpCallsLoading,
    dateRange: getDateRange(),
    stats,
    columns,
    filterableColumns,
    focusedActivity,
    openDrawer,
    isActivityLoading,
    focusedContact,
    openContactDrawer,
    metricFilter,
    metricModalTitle,
    modalActivity,
    modalActivityDrawerOpen,
    modalActivityContact,
    modalActivityContactDrawerOpen,
    modalContacts,
    modalContactsLoading,
    modalAppointments,
    appointmentModalColumns,
    bookedCount,
    modal,
    filteredNpCallActivities,
    npSearchValue,
    npFilterValue,
    modalActivityColumns,
    contactModalColumns,
    contactModalFilterableColumns,
    loadActivity,
    activityHandler,
    closeDrawerHandler,
    handleContactClick,
    closeContactDrawer,
    openMetricModal,
    closeMetricModal,
    modalActivityHandler,
    closeModalActivityDrawer,
    handleModalActivityContactClick,
    closeModalActivityContactDrawer,
    setNpSearchValue,
    setNpFilterValue,
    dateChangeHandler,
  }
}
